import { Flex, Image } from "rebass/styled-components";
import styled from "styled-components";
import { useRecoilState } from "recoil";
import { useHistory } from "react-router-dom";

import { AllstarModalState } from "MVVM/State/modals";
import { ModalType } from "MVVM/@types";
import { analytics } from "MVVM/Analytics";
import { Body, Title3 } from "MVVM/Components/Text";
import { Button } from "MVVM/Components/Button";
import { FloatingContainer, Modal } from "MVVM/Components/Wrappers";
import { ImageWithLoading } from "MVVM/Components/ImageWithLoading";
import Close from "../../../../assets/images/floatingannouncement/close.svg";

/**
 * Companion modal for the welcome-series in-app notifications (sonder
 * welcome-series-v1). Content comes entirely from the Novu message the
 * orchestrator selected (subject/body/primaryAction); dismiss or CTA marks the
 * message read server-side so it never pops again on any device.
 *
 * Built from the same shell as FirstLandTakeover (Modal + FloatingContainer,
 * 640px) so welcome modals and marketing takeovers read as one system.
 */
const WelcomeSeriesModal = () => {
  const [allstarModalState, setAllstarModalState] =
    useRecoilState(AllstarModalState);
  const history = useHistory();

  if (allstarModalState.isOpen !== ModalType.WelcomeSeries) return <></>;

  const { title, body, ctaLabel, ctaUrl, step, imageUrl } =
    allstarModalState.data;
  const { markRead } = allstarModalState.functions;

  const handleClose = () => {
    markRead?.();
    setAllstarModalState({
      data: {},
      isOpen: ModalType.None,
      functions: {},
    });
  };

  const handleCta = () => {
    analytics.track("Promotion - Button Click", {
      type: "welcomeSeriesModal",
      promotionName: `welcome-${step}`,
    });

    const url: string = ctaUrl || "";
    // sonder sends absolute allstar.gg URLs (with utm_source=in-app); route
    // same-site ones through the SPA so the utm-carrying page load is tracked.
    const path = url.replace(/^https?:\/\/(www\.)?allstar\.gg/, "");
    if (path && path !== url) {
      history.push(path);
    } else if (url) {
      window.open(url, "_blank");
    }
    handleClose();
  };

  return (
    <Modal
      isModalOpen={true}
      // Open state lives in AllstarModalState; handleModalClose resets it, so
      // the wrapper's own setModalOpen must be a no-op or markRead runs twice.
      setModalOpen={() => {}}
      handleModalClose={handleClose}
      disableOutsideClose={false}
    >
      <StyledFloatingContainer>
        <CloseIcon size={36} src={Close} onClick={handleClose} />
        {imageUrl && (
          <ImageWithLoading imageSource={imageUrl} aspectRatio={16 / 9} />
        )}
        <Flex flexDirection="column">
          <StyledTitle3
            style={{ marginTop: "16px", marginBottom: "8px" }}
            fontSize={[3, 4, 5, 5, 6]}
          >
            {title}
          </StyledTitle3>
          <Body color="chalk" fontSize={[1, 2, 3]}>
            {body}
          </Body>
        </Flex>
        {ctaLabel && (
          <Flex justifyContent="center">
            <Button mt={[5]} size="small" flex={true} onClick={handleCta}>
              {ctaLabel}
            </Button>
          </Flex>
        )}
      </StyledFloatingContainer>
    </Modal>
  );
};

const StyledFloatingContainer = styled(FloatingContainer)`
  flex-direction: column;
  align-items: stretch;
  justify-content: space-between;
  max-width: 640px;
  position: relative;
`;

const StyledTitle3 = styled(Title3)`
  font-weight: 600;
`;

const CloseIcon = styled(Image)`
  position: absolute;
  right: -20px;
  top: -20px;
  cursor: pointer;
  background-color: ${(props) => props.theme.colors.navy};
  border-radius: 999px;

  &:hover {
    background-color: ${(props) => props.theme.colors.ned};
    transition: 0.3s;
  }
`;

export { WelcomeSeriesModal };
